import { Check, CheckCircle2, X } from 'lucide-react';
import { Button } from '../button/button';
import { cn } from '../../../lib/utils';
import { CARD_CLASS } from './constants';

export interface WizardStep {
  id: string;
  label: string;
  optional?: boolean;
}

export interface WizardFooter {
  back?: { label: string; onClick: () => void };
  skip?: () => void;
  next: { label: string; onClick: () => void; disabled?: boolean };
}

/** Numbered steps; `current` of `done` marks them all complete. */
export function Stepper({
  steps,
  current,
}: {
  steps: WizardStep[];
  current: string;
}) {
  const currentIndex =
    current === 'done'
      ? steps.length
      : steps.findIndex((step) => step.id === current);

  return (
    <ol className="flex flex-wrap gap-1.5" aria-label="Steps">
      {steps.map((step, index) => {
        const state =
          index < currentIndex
            ? 'done'
            : index === currentIndex
              ? 'current'
              : 'next';
        return (
          <li
            key={step.id}
            aria-current={state === 'current' ? 'step' : undefined}
            className={cn(
              'flex items-center gap-2 rounded-full py-1 pl-1 pr-3 text-sm',
              state === 'current'
                ? 'bg-prompt-safecomwhite font-medium text-prompt-safecomvulcan shadow-sm'
                : 'text-prompt-safecomabbey',
            )}
          >
            <span
              className={cn(
                'grid h-6 w-6 place-items-center rounded-full border text-xs font-semibold tabular-nums',
                state === 'done' && 'border-green-700 bg-green-700 text-white',
                state === 'current' &&
                  'border-prompt-safecomoutrageous-orange bg-prompt-safecomoutrageous-orange text-white',
                state === 'next' &&
                  'border-[#CED4DA] bg-prompt-safecomwhite text-prompt-safecomabbey',
              )}
            >
              {state === 'done' ? <Check className="h-3.5 w-3.5" /> : index + 1}
            </span>
            {step.label}
            {step.optional && (
              <span className="text-xs font-normal text-prompt-safecomabbey">
                optional
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function StepTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-base font-semibold text-prompt-safecomvulcan">
      {children}
    </h3>
  );
}

/** The last step of every connection flow. */
export function ReadyStep({ name }: { name: string }) {
  return (
    <div className="flex items-start gap-3">
      <CheckCircle2 className="h-8 w-8 flex-none text-green-700" />
      <div>
        <StepTitle>{name} is ready</StepTitle>
        <p className="text-sm text-prompt-safecomabbey">
          You can now pick it in any simulation.
        </p>
      </div>
    </div>
  );
}

interface WizardFrameProps {
  title: string;
  steps: WizardStep[];
  current: string;
  onClose: () => void;
  footer: WizardFooter;
  children: React.ReactNode;
}

/** Title and steps on top, the current step, then Back / Next. */
export function WizardFrame({
  title,
  steps,
  current,
  onClose,
  footer,
  children,
}: WizardFrameProps) {
  return (
    <section className={cn(CARD_CLASS, 'scroll-mt-4')} aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#00000014] px-5 py-4">
        <h2 className="font-semibold text-prompt-safecomvulcan">{title}</h2>
        <div className="flex items-center gap-2">
          <Stepper steps={steps} current={current} />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-5 p-5">{children}</div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#00000014] px-5 py-3.5">
        {footer.back ? (
          <Button variant="outline" onClick={footer.back.onClick}>
            {footer.back.label}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3">
          {footer.skip && (
            <button
              type="button"
              onClick={footer.skip}
              className="text-sm font-medium text-prompt-safecomoutrageous-orange"
            >
              Skip this step
            </button>
          )}
          <Button
            onClick={footer.next.onClick}
            disabled={footer.next.disabled}
            className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white"
          >
            {footer.next.label}
          </Button>
        </div>
      </div>
    </section>
  );
}
